import { FC, useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material"
import { useSpamCheckMutation } from "../../store"
import { Userbot } from "../../interfaces"
import { getApiErrorMessage } from "@/core/utils"
import { blockBackdropClose } from "../common/dialogUtils"

interface IProps {
  clientId: string
  clientName: string
  open: boolean
  onClose: () => void
}

const statusColor: Record<
  Userbot.SpamCheckResult["status"],
  "success" | "warning" | "error" | "default"
> = {
  ok: "success",
  limited: "warning",
  banned: "error",
  unknown: "default",
}

const statusLabel: Record<Userbot.SpamCheckResult["status"], string> = {
  ok: "ok",
  limited: "limited",
  banned: "banned",
  unknown: "unknown",
}

export const SpamCheckDialog: FC<IProps> = ({
  clientId,
  clientName,
  open,
  onClose,
}) => {
  const [spamCheck, { isLoading, isError, error }] = useSpamCheckMutation()
  const [result, setResult] = useState<Userbot.SpamCheckResult | null>(null)

  useEffect(() => {
    if (!open) {
      setResult(null)
      return
    }
    let cancelled = false
    setResult(null)
    spamCheck({ id: clientId })
      .unwrap()
      .then((res) => {
        if (!cancelled) setResult(res)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [open, clientId, spamCheck])

  return (
    <Dialog
      open={open}
      onClose={blockBackdropClose(onClose)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Spam-check: {clientName}</DialogTitle>
      <DialogContent>
        {isLoading && <CircularProgress />}
        {isError && <Alert severity="error">{getApiErrorMessage(error)}</Alert>}
        {result && (
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography component="span">Статус:</Typography>
              <Chip
                color={statusColor[result.status]}
                label={statusLabel[result.status]}
              />
            </Box>
            {result.rawReply && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Сырой ответ:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    bgcolor: "background.default",
                    p: 1,
                    borderRadius: 1,
                    mt: 0.5,
                    margin: 0,
                  }}
                >
                  {result.rawReply}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Закрыть
        </Button>
      </DialogActions>
    </Dialog>
  )
}
